// src/pages/Reports/FieldVisitsReport.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import visitReportsService from '../../services/visitReports.service';
import employeeService     from '../../services/employee.service';
import regionService       from '../../services/region.service';
import headquarterService  from '../../services/headquarter.service';
import Toast from '../../components/Toast/Toast';
import './FieldVisitsReport.css';

// ── Financial Year default (Apr – Mar) ─────────────────────────────────────
const getCurrentFY = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    fromDate: `${year}-04-01`,
    toDate:   `${year + 1}-03-31`,
  };
};

const fmtDateTime = (d) => d
  ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
  : '—';

const CHART_COLORS = {
  firstVisits:    '#2e7d32',
  followupVisits: '#1976d2',
  dealerVisits:   '#f57c00',
};

const PIE_COLORS = ['#2e7d32', '#1976d2', '#f57c00'];

// ── KPI Card ────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, color, icon }) => (
  <div className="fvr-kpi-card" style={{ '--accent': color }}>
    <div className="fvr-kpi-icon">{icon}</div>
    <div>
      <p className="fvr-kpi-value">{value?.toLocaleString('en-IN') || 0}</p>
      <p className="fvr-kpi-label">{label}</p>
    </div>
  </div>
);

// ── Main Component ──────────────────────────────────────────────────────────
const FieldVisitsReport = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const fy = useMemo(() => getCurrentFY(), []);

  // Filters
  const [filters, setFilters] = useState({
    fromDate:      fy.fromDate,
    toDate:        fy.toDate,
    regionId:      '',
    headquarterId: '',
    employeeId:    '',
    visitType:     'ALL',
    search:        '',
  });

  // Data
  const [summary, setSummary]             = useState({ employeeSummary: [], timeSeries: [], totals: {} });
  const [farmerVisits, setFarmerVisits]   = useState({ rows: [], total: 0, totalPages: 0 });
  const [dealerVisits, setDealerVisits]   = useState({ rows: [], total: 0, totalPages: 0 });

  const [farmerPage, setFarmerPage] = useState(1);
  const [dealerPage, setDealerPage] = useState(1);

  // Dropdowns
  const [employees,    setEmployees]    = useState([]);
  const [regions,      setRegions]      = useState([]);
  const [headquarters, setHeadquarters] = useState([]);

  // UI state
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingFarmer,  setLoadingFarmer]  = useState(false);
  const [loadingDealer,  setLoadingDealer]  = useState(false);
  const [exporting,      setExporting]      = useState(false);
  const [toast,          setToast]          = useState(null);
  const [activeTab,      setActiveTab]      = useState('farmer'); // 'farmer' | 'dealer'

  // ── Build query params (strip empty strings) ────────────────────────────
const queryParams = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) p[k] = v;
    });
    if (isSuperAdmin) {
      const cid = localStorage.getItem('selectedCompanyId');
      if (cid) p.companyId = cid;
    }
    return p;
  }, [filters, isSuperAdmin]);

  // ── Initial dropdowns — pass companyId for SA ─────────────────────────
  useEffect(() => {
    const cid = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : null;
    if (isSuperAdmin && !cid) {
      setToast({ message: 'Please select a company from the navbar first', type: 'error' });
      return;
    }

    employeeService.getAllEmployees?.(cid)
      .then(r => { if (r?.success) setEmployees(r.data || []); })
      .catch(() => {});

    regionService.getAllRegions?.(cid)
      .then(r => { if (r?.success) setRegions(r.data || []); })
      .catch(() => {});

    headquarterService.getAllHeadquarters?.(cid)
      .then(r => { if (r?.success) setHeadquarters(r.data || []); })
      .catch(() => {});
  }, [isSuperAdmin]);

  // ── Fetch summary ───────────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await visitReportsService.getSummary(queryParams);
      if (res.success) setSummary(res.data);
    } catch (err) {
      setToast({ message: 'Failed to load report summary', type: 'error' });
    } finally {
      setLoadingSummary(false);
    }
  }, [queryParams]);

  // ── Fetch farmer visits ─────────────────────────────────────────────────
  const fetchFarmerVisits = useCallback(async () => {
    setLoadingFarmer(true);
    try {
      const res = await visitReportsService.getFarmerVisits(queryParams, farmerPage, 20);
      if (res.success) setFarmerVisits(res.data);
    } catch (_) {
      setToast({ message: 'Failed to load farmer visits', type: 'error' });
    } finally {
      setLoadingFarmer(false);
    }
  }, [queryParams, farmerPage]);

  // ── Fetch dealer visits ─────────────────────────────────────────────────
  const fetchDealerVisits = useCallback(async () => {
    setLoadingDealer(true);
    try {
      const res = await visitReportsService.getDealerVisits(queryParams, dealerPage, 20);
      if (res.success) setDealerVisits(res.data);
    } catch (_) {
      setToast({ message: 'Failed to load dealer visits', type: 'error' });
    } finally {
      setLoadingDealer(false);
    }
  }, [queryParams, dealerPage]);

  // ── Initial dropdowns ───────────────────────────────────────────────────
  useEffect(() => {
    // Employees for the filter dropdown
    employeeService.getAllEmployees?.()
      .then(r => { if (r?.success) setEmployees(r.data || []); })
      .catch(() => {});

    // Regions
    regionService.getAllRegions?.()
      .then(r => { if (r?.success) setRegions(r.data || []); })
      .catch(() => {});

    // Headquarters
    headquarterService.getAllHeadquarters?.()
      .then(r => { if (r?.success) setHeadquarters(r.data || []); })
      .catch(() => {});
  }, []);

  // ── Refetch on filter change ────────────────────────────────────────────
  useEffect(() => {
    fetchSummary();
    setFarmerPage(1);
    setDealerPage(1);
  }, [queryParams]); // eslint-disable-line

  useEffect(() => { fetchFarmerVisits(); }, [fetchFarmerVisits]);
  useEffect(() => { fetchDealerVisits(); }, [fetchDealerVisits]);

  // ── Filter handlers ─────────────────────────────────────────────────────
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      fromDate:      fy.fromDate,
      toDate:        fy.toDate,
      regionId:      '',
      headquarterId: '',
      employeeId:    '',
      visitType:     'ALL',
      search:        '',
    });
  };

  // ── Excel export ────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await visitReportsService.exportExcel(queryParams);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `field-visits-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setToast({ message: 'Report exported successfully!', type: 'success' });
    } catch (_) {
      setToast({ message: 'Failed to export report', type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // ── Chart data ──────────────────────────────────────────────────────────
  const topEmployees = useMemo(
    () => summary.employeeSummary.slice(0, 10),
    [summary]
  );

  const pieData = useMemo(() => {
    const t = summary.totals || {};
    return [
      { name: 'First Visits',    value: t.firstVisits || 0 },
      { name: 'Followup Visits', value: t.followupVisits || 0 },
      { name: 'Dealer Visits',   value: t.dealerVisits || 0 },
    ].filter(d => d.value > 0);
  }, [summary]);

  const totals = summary.totals || {};

  return (
    <div className="fvr-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {isSuperAdmin && !localStorage.getItem('selectedCompanyId') && (
  <div style={{
    padding: '16px 20px', background: '#fff3e0',
    border: '1px solid #ffb74d', borderRadius: 8, marginBottom: 16,
    color: '#e65100', fontWeight: 600,
  }}>
    ⚠️ Please select a company from the top navbar to view the report.
  </div>
)}

      {/* Breadcrumb */}
      <nav className="fvr-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span>
        <span>Reports</span>
        <span>›</span>
        <span>Field Visits</span>
      </nav>

      

      {/* Header */}
      <div className="fvr-header">
        <div>
          <h4 className="fvr-title">Field Visits Report</h4>
          <p className="fvr-subtitle">
            Farmer and dealer visits overview with detailed breakdown
          </p>
        </div>
        <button
          className="fvr-btn-export"
          onClick={handleExport}
          disabled={exporting || loadingSummary}
        >
          {exporting ? 'Exporting...' : '⬇ Export Excel'}
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="fvr-filters-card">
        <div className="fvr-filters-grid">
          <div className="fvr-filter">
            <label>From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => updateFilter('fromDate', e.target.value)}
            />
          </div>
          <div className="fvr-filter">
            <label>To Date</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => updateFilter('toDate', e.target.value)}
            />
          </div>
          <div className="fvr-filter">
            <label>Visit Type</label>
            <select
              value={filters.visitType}
              onChange={(e) => updateFilter('visitType', e.target.value)}
            >
              <option value="ALL">All Types</option>
              <option value="FIRST_VISIT">First Visit</option>
              <option value="FOLLOWUP">Followup</option>
              <option value="DEALER">Dealer</option>
            </select>
          </div>
          <div className="fvr-filter">
            <label>Region</label>
            <select
              value={filters.regionId}
              onChange={(e) => updateFilter('regionId', e.target.value)}
            >
              <option value="">All Regions</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>{r.name || r.regionName}</option>
              ))}
            </select>
          </div>
          <div className="fvr-filter">
            <label>Headquarter</label>
            <select
              value={filters.headquarterId}
              onChange={(e) => updateFilter('headquarterId', e.target.value)}
            >
              <option value="">All Headquarters</option>
              {headquarters
                .filter(h => !filters.regionId || String(h.regionId) === String(filters.regionId))
                .map((h) => (
                  <option key={h.id} value={h.id}>{h.name || h.headquarterName}</option>
                ))}
            </select>
          </div>
          <div className="fvr-filter">
            <label>Employee</label>
            <select
              value={filters.employeeId}
              onChange={(e) => updateFilter('employeeId', e.target.value)}
            >
              <option value="">All Employees</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName} ({e.employeeCode})
                </option>
              ))}
            </select>
          </div>
          <div className="fvr-filter fvr-filter-wide">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search farmer/dealer/employee…"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
            />
          </div>
          <button className="fvr-btn-reset" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="fvr-kpi-grid">
        <KpiCard icon="🌾" label="First Visits"    value={totals.firstVisits}    color="#2e7d32"/>
        <KpiCard icon="🔁" label="Followup Visits" value={totals.followupVisits} color="#1976d2"/>
        <KpiCard icon="🏪" label="Dealer Visits"   value={totals.dealerVisits}   color="#f57c00"/>
        <KpiCard icon="📊" label="Total Visits"    value={totals.total}          color="#6a1b9a"/>
      </div>

  {/* ── Detail Tabs ── */}
      <div className="fvr-section-card">
        <div className="fvr-tabs">
          <button
            className={`fvr-tab ${activeTab === 'farmer' ? 'active' : ''}`}
            onClick={() => setActiveTab('farmer')}
          >
            🌾 Farmer Visits ({farmerVisits.total})
          </button>
          <button
            className={`fvr-tab ${activeTab === 'dealer' ? 'active' : ''}`}
            onClick={() => setActiveTab('dealer')}
          >
            🏪 Dealer Visits ({dealerVisits.total})
          </button>
        </div>

        {activeTab === 'farmer' ? (
          <>
            <div className="fvr-table-wrap">
              <table className="fvr-table">
                <thead>
                  <tr>
                    <th>Visit Time</th>
                    <th>Type</th>
                    <th>Employee</th>
                    <th>Farmer</th>
                    <th>Village</th>
                    <th>Crop</th>
                    <th>Reason / Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingFarmer ? (
                    <tr><td colSpan="7" className="fvr-loading">Loading…</td></tr>
                  ) : farmerVisits.rows.length === 0 ? (
                    <tr><td colSpan="7" className="fvr-empty">No farmer visits found</td></tr>
                  ) : (
                    farmerVisits.rows.map((v) => (
                      <tr key={v.id}>
                        <td>{fmtDateTime(v.visitTime)}</td>
                        <td>
                          <span className={`fvr-type-badge ${v.visitType === 'FIRST_VISIT' ? 'first' : 'followup'}`}>
                            {v.visitType === 'FIRST_VISIT' ? 'First' : 'Followup'}
                          </span>
                        </td>
                        <td>
                          <div className="fvr-stack">
                            <strong>{v.employeeName}</strong>
                            <small>{v.employeeCode} · {v.roleName}</small>
                          </div>
                        </td>
                        <td>
                          <div className="fvr-stack">
                            <strong>{v.farmerName}</strong>
                            <small>{v.farmerMobile}</small>
                          </div>
                        </td>
                        <td>{v.village || '—'}</td>
                        <td>{v.crop ? `${v.crop}${v.acre ? ` · ${v.acre}` : ''}` : '—'}</td>
                        <td className="fvr-remark">{v.reason || v.remark || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={farmerPage}
              totalPages={farmerVisits.totalPages}
              onPageChange={setFarmerPage}
            />
          </>
        ) : (
          <>
            <div className="fvr-table-wrap">
              <table className="fvr-table">
                <thead>
                  <tr>
                    <th>Visit Time</th>
                    <th>Employee</th>
                    <th>Dealer Shop</th>
                    <th>Owner</th>
                    <th>Mobile</th>
                    <th>State</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingDealer ? (
                    <tr><td colSpan="7" className="fvr-loading">Loading…</td></tr>
                  ) : dealerVisits.rows.length === 0 ? (
                    <tr><td colSpan="7" className="fvr-empty">No dealer visits found</td></tr>
                  ) : (
                    dealerVisits.rows.map((v) => (
                      <tr key={v.id}>
                        <td>{fmtDateTime(v.visitTime)}</td>
                        <td>
                          <div className="fvr-stack">
                            <strong>{v.employeeName}</strong>
                            <small>{v.employeeCode} · {v.roleName}</small>
                          </div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{v.dealerShopName}</td>
                        <td>{v.ownerName || '—'}</td>
                        <td>{v.dealerMobile || '—'}</td>
                        <td>{v.dealerState || '—'}</td>
                        <td className="fvr-remark">{v.remark || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={dealerPage}
              totalPages={dealerVisits.totalPages}
              onPageChange={setDealerPage}
            />
          </>
        )}
      </div>
      
      {/* ── Employee Summary Table ── */}
      <div className="fvr-section-card">
        <h6 className="fvr-section-title">Employee Summary</h6>
        <div className="fvr-table-wrap">
          <table className="fvr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Role</th>
                <th className="fvr-num">First</th>
                <th className="fvr-num">Followup</th>
                <th className="fvr-num">Dealer</th>
                <th className="fvr-num">Total</th>
              </tr>
            </thead>
            <tbody>
              {loadingSummary ? (
                <tr><td colSpan="8" className="fvr-loading">Loading…</td></tr>
              ) : summary.employeeSummary.length === 0 ? (
                <tr><td colSpan="8" className="fvr-empty">No data found</td></tr>
              ) : (
                summary.employeeSummary.map((e, i) => (
                  <tr key={e.employeeId}>
                    <td>{i + 1}</td>
                    <td className="fvr-code">{e.employeeCode || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{e.employeeName}</td>
                    <td><span className="fvr-role-pill">{e.roleName}</span></td>
                    <td className="fvr-num">{e.firstVisits}</td>
                    <td className="fvr-num">{e.followupVisits}</td>
                    <td className="fvr-num">{e.dealerVisits}</td>
                    <td className="fvr-num fvr-total">{e.totalVisits}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

    

      {/* ── Charts Grid ── */}
      <div className="fvr-charts-grid">

        {/* Bar Chart — Visits per employee */}
        <div className="fvr-chart-card fvr-chart-wide">
          <h6 className="fvr-chart-title">Top 10 Employees by Visits</h6>
          {loadingSummary ? (
            <div className="fvr-chart-loading">Loading…</div>
          ) : topEmployees.length === 0 ? (
            <div className="fvr-chart-empty">No data for selected filters</div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={topEmployees} margin={{ top: 10, right: 20, bottom: 60, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
                <XAxis
                  dataKey="employeeName"
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                  tick={{ fontSize: 11, fill: '#555' }}
                  height={80}
                />
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip />
                <Legend />
                <Bar dataKey="firstVisits"    stackId="a" fill={CHART_COLORS.firstVisits}    name="First"/>
                <Bar dataKey="followupVisits" stackId="a" fill={CHART_COLORS.followupVisits} name="Followup"/>
                <Bar dataKey="dealerVisits"   stackId="a" fill={CHART_COLORS.dealerVisits}   name="Dealer"/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pie Chart — Visit breakdown */}
        <div className="fvr-chart-card">
          <h6 className="fvr-chart-title">Visit Type Breakdown</h6>
          {loadingSummary ? (
            <div className="fvr-chart-loading">Loading…</div>
          ) : pieData.length === 0 ? (
            <div className="fvr-chart-empty">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Line Chart — Visits over time */}
        <div className="fvr-chart-card fvr-chart-full">
          <h6 className="fvr-chart-title">Visits Over Time</h6>
          {loadingSummary ? (
            <div className="fvr-chart-loading">Loading…</div>
          ) : summary.timeSeries.length === 0 ? (
            <div className="fvr-chart-empty">No data for selected period</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={summary.timeSeries} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="firstVisits"    stroke={CHART_COLORS.firstVisits}    name="First"    strokeWidth={2}/>
                <Line type="monotone" dataKey="followupVisits" stroke={CHART_COLORS.followupVisits} name="Followup" strokeWidth={2}/>
                <Line type="monotone" dataKey="dealerVisits"   stroke={CHART_COLORS.dealerVisits}   name="Dealer"   strokeWidth={2}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Pagination ─────────────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="fvr-pagination">
      <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}>← Prev</button>
      <span>Page {page} of {totalPages}</span>
      <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>Next →</button>
    </div>
  );
};

export default FieldVisitsReport;